/**
 * SoleadLab 2.0 — Scoring API Client (D2.4)
 *
 * Thin fetch wrappers for the D2.3 scoring endpoints.
 * All scoring logic is backend-owned; this service is strictly read/trigger.
 */

import type {
  ListScoresResponse,
  ScoreEntry,
  ScoreHistoryResponse,
  ScoreComponentsResponse,
} from '@/types/scoring';
import { apiFetch } from './apiFetch';

// ─── Scoring API functions ──────────────────────────────────────────────────

/**
 * GET /api/v2/sites/:siteId/scores
 * List all scores for a site (site-level + per-asset), three-state model.
 */
export async function listScores(siteId: string): Promise<ListScoresResponse> {
  return apiFetch(`/api/v2/sites/${siteId}/scores`);
}

/**
 * GET /api/v2/sites/:siteId/scores/latest
 * Latest score for a specific (entity, score_type) — three-state response.
 */
export async function getLatestScore(
  siteId: string,
  entityType: 'asset' | 'site',
  entityId: string,
  scoreType: string,
): Promise<ScoreEntry> {
  const params = new URLSearchParams({ entity_type: entityType, entity_id: entityId, score_type: scoreType });
  return apiFetch(`/api/v2/sites/${siteId}/scores/latest?${params}`);
}

/**
 * GET /api/v2/sites/:siteId/scores/history
 * Paginated score history for an entity + score type.
 */
export async function getScoreHistory(
  siteId: string,
  entityType: 'asset' | 'site',
  entityId: string,
  scoreType: string,
  limit = 20,
  offset = 0,
): Promise<ScoreHistoryResponse> {
  const params = new URLSearchParams({
    entity_type: entityType,
    entity_id: entityId,
    score_type: scoreType,
    limit: String(limit),
    offset: String(offset),
  });
  return apiFetch(`/api/v2/sites/${siteId}/scores/history?${params}`);
}

/**
 * GET /api/v2/sites/:siteId/scores/:scoreSnapshotId/components
 * Component-level drilldown for a specific score snapshot.
 */
export async function getScoreComponents(
  siteId: string,
  scoreSnapshotId: string,
): Promise<ScoreComponentsResponse> {
  return apiFetch(`/api/v2/sites/${siteId}/scores/${scoreSnapshotId}/components`);
}

/**
 * POST /api/v2/sites/:siteId/scores/compute
 * Trigger score computation for all assets and the site.
 */
export async function computeScores(
  siteId: string,
  options?: { asset_id?: string; include_site?: boolean },
): Promise<unknown> {
  return apiFetch(`/api/v2/sites/${siteId}/scores/compute`, {
    method: 'POST',
    body: JSON.stringify(options || {}),
  }, 30_000); // longer timeout for compute
}
